<template>
  <div :class="[{ purpose: this.data?.body?.purpose }]" style="width: 600px;">
    <q-form @submit="this.onSubmit" class="form-1c">
      <!-- Инициатор заявки - первым -->
      <div class="form-1c__row">
        <label class="form-1c__label">Инициатор заявки</label>
        <div class="form-1c__field">
          <CustomSelect 
            v-if="this.initiatorTypesLoaded && this.initiatorTypes.length > 0"
            :options="this.initiatorTypes" 
            optionValueKey="provider_request_initiator_type_id"
            optionLabelKey="provider_request_initiator_type_name" 
            placeholder="Выберите инициатора"
            :value="this.formData.provider_request_initiator_type_id || ''"
            @change="(value, row) => this.onChange('provider_request_initiator_type_id', value, row)" />
          <div v-else-if="this.initiatorTypesLoaded && this.initiatorTypes.length === 0" class="form-1c__field--warning">
            Нет типов инициаторов
          </div>
          <div v-else class="form-1c__field--placeholder">Загрузка...</div>
        </div>
      </div>

      <!-- Материал -->
      <div class="form-1c__row">
        <label class="form-1c__label">Материал <span class="required">*</span></label>
        <div class="form-1c__field">
          <!-- Пока инициатор не выбран - материал выбрать нельзя -->
          <div v-if="!this.formData.provider_request_initiator_type_id" class="form-1c__field--placeholder">
            Сначала выберите инициатора
          </div>
          <CustomSelect
            v-else-if="this.materialsLoaded && (this.materials || []).length > 0"
            :options="this.materials || []"
            optionValueKey="material_id"
            optionLabelKey="material_name"
            placeholder="Выберите материал"
            :required="true"
            :value="this.formData.material_id || ''"
            @change="(value, row) => this.onChange('material_id', value, row)"
          />
          <div
            v-else-if="this.materialsLoaded && (this.materials || []).length === 0"
            class="form-1c__field--warning"
          >
            Материала нет
          </div>
          <div v-else class="form-1c__field--placeholder">Загрузка...</div>
        </div>
      </div>

      <!-- Склад -->
      <div class="form-1c__row">
        <label class="form-1c__label">Склад</label>
        <div class="form-1c__field">
          <!-- Прочерк если инициатор не выбран или выбран подрядчик -->
          <div v-if="!this.selectedInitiatorType || this.isContractorInitiator" class="form-1c__field--placeholder">—</div>
          <template v-else>
            <CustomSelect 
              v-if="this.skladOptions.length > 1"
              :options="this.skladOptions || []" optionValueKey="sbs_sklad_id"
              optionLabelKey="naimenovanie" placeholder="Выберите склад"
              :value="this.formData.sbs_sklad_id || ''"
              @change="(value, row) => this.onChange('sbs_sklad_id', value, row)" />
            <div v-else-if="this.skladOptions.length === 1" class="form-1c__field--readonly">
              {{ this.skladOptions[0].naimenovanie }}
            </div>
            <div v-else-if="this.skladLoaded && this.skladOptions.length === 0" class="form-1c__field--warning">
              Нет склада
            </div>
            <div v-else class="form-1c__field--placeholder">—</div>
            <div class="form-1c__hint">На этот склад будет оприходован материал</div>
          </template>
        </div>
      </div>

      <!-- Договор поставки -->
      <div class="form-1c__row">
        <label class="form-1c__label">Договор поставки</label>
        <div class="form-1c__field">
          <CustomSelect 
            v-if="this.contractorAgreements.length > 1"
            :options="this.contractorAgreements" optionValueKey="contractor_agreement_list_id"
            optionLabelKey="agreement_display_name" placeholder="Выберите договор"
            :value="this.formData.contractor_agreement_list_id || ''"
            @change="(value, row) => this.onChange('contractor_agreement_list_id', value, row)" />
          <div v-else-if="this.contractorAgreements.length === 1" class="form-1c__field--readonly">
            {{ this.contractorAgreements[0].agreement_display_name }}
          </div>
          <div v-else-if="this.agreementsLoaded && this.contractorAgreements.length === 0" class="form-1c__field--warning">
            Нет договора
          </div>
          <div v-else class="form-1c__field--placeholder">—</div>
        </div>
      </div>

      <!-- Две колонки для коротких полей -->
      <div class="form-1c__grid">
        <div class="form-1c__row">
          <label class="form-1c__label">Кол-во <span class="required">*</span></label>
          <div class="form-1c__field">
            <CustomInput placeholder="0" type="number" :required="true" :allowNegative="false"
              :value="this.formData.qty_sell === 0 ? '0' : this.formData.qty_sell || ''"
              @change_input="(value) => onChange('qty_sell', value)"
              :suffix="this.selectedMaterial ? this.selectedMaterial?.unit_name : ''" />
          </div>
        </div>

        <div class="form-1c__row">
          <label class="form-1c__label">Дата отгрузки <span class="required">*</span></label>
          <div class="form-1c__field">
            <CustomDate placeholder="Выберите дату" :required="true"
              @change_date="(value) => onChange('date_shipping', value)" :value="this.formData.date_shipping"
              :fullWidth="true" :disablePast="true" />
          </div>
        </div>

        <div class="form-1c__row" v-if="this.amount">
          <label class="form-1c__label">Сумма</label>
          <div class="form-1c__field form-1c__field--sum">
            {{ numberWithCommas(this.amount) }} ₸
          </div>
        </div>
      </div>

      <CustomModalFooter :btn-loading="this.loading" />
    </q-form>
  </div>
</template>
<script>

import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomDate from "@/components/form/CustomDate";
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import { createEntranceMaterialRequest, getEntranceMaterials, getSkladOptions, getPurchaseTypes, getContractorAgreementsByMaterial, getInitiatorTypes } from "@/views/ResidentialSettings/services";
import { numberWithCommas } from "@/services"
import { useAppStore } from "@/services/store/app";

export default {
  name: "add-material-request",
  components: { CustomInput, CustomDate, CustomSelect, CustomModalFooter },
  props: ['data'],
  data() {
    return {
      materials: [],
      materialsLoaded: false,
      skladOptions: [],
      purchaseTypes: [],
      initiatorTypes: [],
      contractorAgreements: [],
      agreementsLoaded: false,
      skladLoaded: false,
      initiatorTypesLoaded: false,
      loading: false,
      selectedMaterial: null,
      selectedPurchaseType: null,
      selectedInitiatorType: null,
      formData: {
        material_id: '',
        qty_sell: '',
        date_shipping: '',
        sbs_sklad_id: '',
        provider_request_purchase_type_id: '',
        provider_request_initiator_type_id: '',
        contractor_agreement_list_id: '',
      }
    }
  },
  computed: {
    amount() {
      if (!this.selectedMaterial || !this.formData?.qty_sell) return null
      const sum = this.formData?.qty_sell * (this.selectedMaterial?.material_price || 0)
      return Math.round(sum * 100) / 100
    },
    projectId() {
      return this.data?.data?.params?.project_id || this.data?.data?.projectId || null
    },
    isContractorInitiator() {
      return this.selectedInitiatorType?.provider_request_initiator_type_code === 'CONTRACTOR'
    }
  },
  methods: {
    numberWithCommas,
    onChange(key, value, row) {
      this.formData[key] = value
      if (key === 'material_id') {
        this.selectedMaterial = row
        // Сбрасываем договор и склад при смене материала
        this.formData.contractor_agreement_list_id = ''
        this.formData.sbs_sklad_id = ''
        this.contractorAgreements = []
        this.skladOptions = []
        this.agreementsLoaded = false
        this.skladLoaded = false
        // Загружаем склады
        this.getSkladOptions()
        // Загружаем договора для выбранного материала
        if (value) {
          this.getContractorAgreements(value)
        }
      }
      if (key === 'provider_request_initiator_type_id') {
        this.selectedInitiatorType = row
        // При смене инициатора перечитываем материалы (в БД теперь есть фильтр по инициатору)
        // и сбрасываем выбранный материал/зависимые поля, чтобы не оставить "несовместимое" значение.
        this.formData.material_id = ''
        this.formData.qty_sell = ''
        this.formData.contractor_agreement_list_id = ''
        this.contractorAgreements = []
        this.agreementsLoaded = false
        this.selectedMaterial = null
        this.getMaterials()
        // Если выбран подрядчик - сбрасываем склад
        if (row?.provider_request_initiator_type_code === 'CONTRACTOR') {
          this.formData.sbs_sklad_id = ''
        }
      }
    },
    async getMaterials() {
      this.materialsLoaded = false
      const sbs_project_id = this.data?.data?.params?.sbs_project_id
      if (sbs_project_id == null) {
        this.materials = []
        this.materialsLoaded = true
        return
      }
      const params = {
        resident_id: this.data?.data?.params?.resident_id,
        project_type_id: this.data?.data?.params?.project_type_id,
        project_entrance_id: this.data?.data?.params?.project_entrance_id,
        sbs_project_id,
      }
      if (this.formData?.provider_request_initiator_type_id) {
        params.provider_request_initiator_type_id = this.formData.provider_request_initiator_type_id
      }
      const res = await getEntranceMaterials(params)
      this.materials = res || []
      this.materialsLoaded = true
    },
    async getSkladOptions() {
      this.skladLoaded = false
      const paramsObj = this.data?.data?.params || {}
      if (paramsObj.sbs_project_id == null) {
        console.warn('Проект УСП не найден для получения списка складов')
        this.skladLoaded = true
        return
      }
      const params = { sbs_project_id: paramsObj.sbs_project_id }
      if (paramsObj.sbs_sklad_id != null) {
        params.sbs_sklad_id = paramsObj.sbs_sklad_id
      }
      if (this.formData?.sbs_sklad_id != null) {
        params.sbs_sklad_id = this.formData.sbs_sklad_id
      }
      const res = await getSkladOptions(params)
      this.skladOptions = res || []
      this.skladLoaded = true
      // Автоподстановка если склад один
      if (this.skladOptions.length === 1) {
        this.formData.sbs_sklad_id = this.skladOptions[0].sbs_sklad_id
      }
    },
    async getContractorAgreements(material_id) {
      this.agreementsLoaded = false
      const sbs_project_id = this.data?.data?.params?.sbs_project_id
      if (sbs_project_id == null) {
        this.contractorAgreements = []
        this.agreementsLoaded = true
        return
      }
      const res = await getContractorAgreementsByMaterial(material_id, sbs_project_id)
      this.contractorAgreements = res || []
      this.agreementsLoaded = true
      // Автоподстановка если договор один
      if (this.contractorAgreements.length === 1) {
        this.formData.contractor_agreement_list_id = this.contractorAgreements[0].contractor_agreement_list_id
      }
    },
    async onSubmit() {
      const sbs_project_id = this.data?.data?.params?.sbs_project_id
      if (sbs_project_id == null) {
        this.showErrorSnackbar('Не указан проект УСП (sbs_project_id)')
        return
      }
      this.loading = true
      const payload = {
        material_id: this.formData.material_id,
        qty_sell: this.formData.qty_sell,
        date_shipping: this.formData.date_shipping,
        provider_request_purchase_type_id: this.formData.provider_request_purchase_type_id,
        provider_request_initiator_type_id: this.formData.provider_request_initiator_type_id || null,
        contractor_agreement_list_id: this.formData.contractor_agreement_list_id || null,
      }
      if (this.formData.sbs_sklad_id) {
        payload.sbs_sklad_id = this.formData.sbs_sklad_id
      }
      const requestParams = { ...this.data?.data?.params, sbs_project_id }
      const res = await createEntranceMaterialRequest(payload, requestParams)
      this.loading = false
      if (!res) return
      if (this.data?.onSubmit)
        await this.data?.onSubmit(res)
      this.showSucceedSnackbar()
      this.closeModal()
    },
    async setDefaultPurchaseType() {
      const res = await getPurchaseTypes()
      this.purchaseTypes = res || []
      const directPurchase = this.purchaseTypes.find(pt => pt.provider_request_purchase_type_code === 'DIRECT')
      if (directPurchase) {
        this.formData.provider_request_purchase_type_id = directPurchase.provider_request_purchase_type_id
        this.selectedPurchaseType = directPurchase
      }
    },
    async getInitiatorTypes() {
      this.initiatorTypesLoaded = false
      const res = await getInitiatorTypes()
      this.initiatorTypes = res || []
      this.initiatorTypesLoaded = true
    },
  },
  setup() {
    const { closeModal } = useAppStore()
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar()
    return { showSucceedSnackbar, showErrorSnackbar, closeModal }
  },
  mounted() {
    this.setDefaultPurchaseType()
    this.getSkladOptions()
    this.getInitiatorTypes()
  }
}
</script>

<style scoped lang="scss">
.form-1c {
  margin-top: 10px;
  
  &__row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
  }
  
  &__label {
    width: 130px;
    min-width: 130px;
    font-size: 13px;
    color: #333;
    padding-top: 8px;
    padding-right: 10px;
    text-align: right;
    
    .required {
      color: red;
    }
  }
  
  &__field {
    flex: 1;
    min-width: 0;
    
    &--readonly {
      padding: 8px 10px;
      background-color: #f8f8f8;
      border: 1px solid #ddd;
      border-radius: 3px;
      font-size: 13px;
      color: #333;
    }
    
    &--placeholder {
      padding: 8px 10px;
      color: #999;
      font-size: 13px;
    }
    
    &--warning {
      padding: 8px 10px;
      background-color: #fff3cd;
      border: 1px solid #ffc107;
      border-radius: 3px;
      font-size: 12px;
      color: #856404;
    }
    
    &--sum {
      padding: 8px 10px;
      font-size: 14px;
      font-weight: 600;
      color: #2e7d32;
    }
  }
  
  &__hint {
    font-size: 11px;
    color: #666;
    margin-top: 4px;
    font-style: italic;
  }
  
  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 20px;
    margin-top: 5px;
    margin-bottom: 15px;
  }
}

@media (max-width: 600px) {
  .form-1c {
    &__grid {
      grid-template-columns: 1fr;
    }
    
    &__row {
      flex-direction: column;
    }
    
    &__label {
      width: 100%;
      text-align: left;
      padding-top: 0;
      margin-bottom: 4px;
    }
  }
}
</style>
